import { useEffect } from "react";
import { Form, Input, InputNumber, Modal, Switch } from "antd";
import {
  MedicinePublication,
  MedicinePublicationFormValues,
  MedicinePublicationKind,
  PUBLICATION_LABELS,
} from "./types";

type MedicinePublicationModalProps = {
  open: boolean;
  saving: boolean;
  kind: MedicinePublicationKind;
  publication?: MedicinePublication;
  onCancel: () => void;
  onSubmit: (values: MedicinePublicationFormValues) => Promise<void>;
};

export const MedicinePublicationModal = ({
  open,
  saving,
  kind,
  publication,
  onCancel,
  onSubmit,
}: MedicinePublicationModalProps) => {
  const [form] = Form.useForm<MedicinePublicationFormValues>();
  const labels = PUBLICATION_LABELS[kind];

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      id: publication?.id,
      title: publication?.title || "",
      authors: publication?.authors || "",
      source: publication?.source || "",
      linkUrl: publication?.linkUrl || "",
      publishedYear: publication?.publishedYear ?? null,
      isActive: publication?.isActive ?? true,
    });
  }, [form, publication, open]);

  const handleSubmit = async () => {
    let values: MedicinePublicationFormValues;

    try {
      values = await form.validateFields();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      throw error;
    }

    await onSubmit(values);
    form.resetFields();
  };

  return (
    <Modal
      title={publication ? labels.editTitle : labels.createTitle}
      open={open}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={640}
      destroyOnClose
    >
      <Form<MedicinePublicationFormValues> form={form} layout="vertical">
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="title"
          label="Заголовок"
          rules={[{ required: true, message: "Укажите заголовок" }]}
        >
          <Input.TextArea rows={2} maxLength={1024} showCount />
        </Form.Item>

        <Form.Item
          name="authors"
          label="Авторы"
          rules={[{ required: true, message: "Укажите авторов" }]}
        >
          <Input.TextArea rows={2} placeholder="Иванов И.И., Петров П.П." />
        </Form.Item>

        <Form.Item name="source" label="Источник" extra={labels.sourceHint}>
          <Input.TextArea
            rows={2}
            placeholder="Sports medicine research and practice. 2025;15(1):5-16 | doi.org/10…"
          />
        </Form.Item>

        <Form.Item
          name="linkUrl"
          label="Ссылка"
          extra="Без ссылки карточка на сайте не кликабельна"
          rules={[
            {
              validator: (_, value) =>
                !value || /^https?:\/\//.test(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "Ссылка должна начинаться с http:// или https://"
                      )
                    ),
            },
          ]}
        >
          <Input placeholder="https://" />
        </Form.Item>

        <Form.Item name="publishedYear" label="Год">
          <InputNumber
            min={1900}
            max={new Date().getFullYear() + 1}
            style={{ width: 140 }}
            placeholder="2025"
          />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Показывать на сайте"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};
